import { Navigate } from 'react-router-dom';
import { useProvince } from '../hooks/useProvince';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { WAR_ITEMS } from '../constants/warNav';
import { HubCard, type HubCardStat } from '../components/shell/HubCard';
import type { Province } from '../types/province';
import type { SiblingItem } from '../types/nav';

function computeStat(item: SiblingItem, province: Province | null): HubCardStat {
  if (!province) return { primary: 'Loading…' };

  switch (item.key) {
    case 'attack': {
      const count = province.military.returningArmies.length;
      const primary = count === 0 ? 'All armies home' : `${count} arm${count === 1 ? 'y' : 'ies'} returning`;
      return count > 0 ? { primary, attention: 'warning' } : { primary };
    }
    case 'mystics': {
      const wizards = province.population.wizards;
      if (wizards === 0) return { primary: 'No wizards' };
      const totalAcres = province.territory.totals.acres;
      const wpa = totalAcres > 0 ? (wizards / totalAcres).toFixed(2) : '0.00';
      return { primary: `${wpa} WPA · ${Math.round(province.resources.mana)}% mana` };
    }
    case 'thievery': {
      const thieves = province.population.thieves;
      if (thieves === 0) return { primary: 'No thieves' };
      const totalAcres = province.territory.totals.acres;
      const tpa = totalAcres > 0 ? thieves / totalAcres : 0;
      return { primary: `${tpa.toFixed(2)} TPA · ${Math.round(province.resources.stealth)}% stealth` };
    }
    default:
      return { primary: '' };
  }
}

/**
 * SF-146 Component 5: `/war` hub screen, routable under `AppShellLayout`. Structurally identical
 * to `RealmHub.tsx` — a navigation surface, not a data-display page — the desktop guard fires
 * before any province data check, and all three cards always render regardless of province load
 * state; only the stat line degrades to a placeholder.
 */
export function WarHub() {
  const platform = useAppShellPlatform();
  const { province } = useProvince();

  if (platform === 'desktop') {
    return <Navigate to={WAR_ITEMS[0].route} replace />;
  }

  return (
    <div>
      <h1 className="text-lg font-bold text-game-50 mb-4">War</h1>
      <div className="flex flex-col gap-3">
        {WAR_ITEMS.map((item) => (
          <HubCard key={item.key} item={item} stat={computeStat(item, province)} />
        ))}
      </div>
    </div>
  );
}
